import { useMutation } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { LoadMore } from "@/components/common/LoadMore";
import { NoBackend } from "@/components/common/NoBackend";
import { Button } from "@/components/design/Button";
import {
  ListingApplicationState,
  MyListingApplicationsDocument,
  SortOrder,
  WithdrawListingApplicationDocument,
  type MyListingApplicationFragment,
} from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { usePagedQuery } from "@/lib/use-paged-query";
import { userErrorMessage } from "@/lib/user-error";
import { ListingReference } from "@/pages/share/listings/ListingReference";
import { forgetCanApply } from "./application-cache";
import { LISTING_APPLICATION_STATE_LABELS } from "./listing-application-state";

// SHARE · Meine Bewerbungen: jede eigene Bewerbung mit Ausschreibung und deren Kennziffer, Zustand und
// Datum, in jedem Zustand (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §4)).
// Die eingereichten Dokumente stehen hier bewusst nicht: sie liegen in der Organisationsakte, und die
// bewerbende Person liest sie nicht mehr, nicht einmal ihre Namen.
//
// Zurückziehen bietet die Seite nur an, wo genesis `actions.canWithdraw` meldet, und erst nach einer
// Rückfrage: der Rückzug ist endgültig (@adr SPARK-0011-destructive-actions-require-confirmation@1).
export function MyApplicationsPage() {
  const intl = useIntl();
  const input = useMemo(() => ({ order: { receivedOn: SortOrder.Desc } }), []);
  const { nodes, loading, error, hasMore, loadingMore, loadMoreFailed, loadMore } =
    usePagedQuery<MyListingApplicationFragment>(MyListingApplicationsDocument, {
      connectionKey: "myListingApplications",
      input,
      pageSize: 24,
    });
  const [withdraw] = useMutation(WithdrawListingApplicationDocument);
  const [withdrawing, setWithdrawing] = useState<MyListingApplicationFragment | null>(null);
  const [pending, setPending] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  async function confirmWithdraw() {
    if (!withdrawing || pending) return;
    const { id, listingId } = withdrawing;
    setPending(true);
    setWithdrawError(null);
    try {
      await withdraw({
        variables: { input: { id, dryRun: false } },
        update: (cache) => forgetCanApply(cache, listingId),
      });
      setWithdrawing(null);
    } catch (e) {
      setWithdrawError(userErrorMessage(intl, e));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="MyListingApplications">
      <div className="mb-5">
        <BackButton />
      </div>
      <h1 className="smycles-page-title">
        <FormattedMessage id="share.applications.title" defaultMessage="Meine Bewerbungen" />
      </h1>
      <p className="mt-4 max-w-md text-smycles-body text-ink">
        <FormattedMessage
          id="share.applications.subtitle"
          defaultMessage="Deine Bewerbungen auf Ausschreibungen und wie weit sie sind."
        />
      </p>

      <div className="mt-8 max-w-3xl">
        {loading ? (
          <div className="text-smycles-label text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Laden…" />
          </div>
        ) : error ? (
          <p role="alert" className="text-smycles-label text-ink-muted">
            {userErrorMessage(intl, error)}
          </p>
        ) : nodes.length === 0 ? (
          <>
            <NoBackend
              label={intl.formatMessage({ id: "share.applications.empty", defaultMessage: "Noch keine Bewerbung" })}
            />
            <Link
              to="/share/open-listings"
              className="mt-4 inline-flex items-center gap-1 text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink-accent"
            >
              <FormattedMessage id="share.applications.toOpenListings" defaultMessage="Offene Ausschreibungen" />
              <span aria-hidden="true">↗</span>
            </Link>
          </>
        ) : (
          <>
            <ul className="box divide-y divide-ink/5 bg-surface px-5">
              {nodes.map((application) => {
                const received = formatDateTime(application.receivedOn, intl.locale);
                const decided = formatDateTime(application.decidedOn, intl.locale);
                return (
                  <li
                    key={application.id}
                    id={`my-application-${application.id}`}
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-4"
                  >
                    <div className="min-w-0">
                      <Link to={`/share/open-listings/${application.listingId}`}
                        className="block truncate text-smycles-body font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui">
                        {application.listing.name || (
                          <FormattedMessage id="share.listings.untitled" defaultMessage="Ohne Titel" />
                        )}
                      </Link>
                      {application.companyName ? <p className="text-smycles-body">{application.companyName}</p> : null}
                      <div className="smycles-field-meta text-ink-muted">
                        <ListingReference reference={application.listing.reference} />
                        {received ? (
                          <>
                            {" · "}
                            <FormattedMessage
                              id="share.applications.receivedOn"
                              defaultMessage="Eingegangen am {date}"
                              values={{ date: received }}
                            />
                          </>
                        ) : null}
                        {decided ? (
                          <>
                            {" · "}
                            {/* genesis setzt decidedOn auch beim Rückzug; der ist aber keine Entscheidung. */}
                            {application.state === ListingApplicationState.Withdrawn ? (
                              <FormattedMessage
                                id="share.applications.withdrawnOn"
                                defaultMessage="Zurückgezogen am {date}"
                                values={{ date: decided }}
                              />
                            ) : (
                              <FormattedMessage
                                id="share.applications.decidedOn"
                                defaultMessage="Entschieden am {date}"
                                values={{ date: decided }}
                              />
                            )}
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span id={`my-application-state-${application.id}`} className="smycles-field-value font-medium">
                        {intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[application.state])}
                      </span>
                      {application.actions.canWithdraw ? (
                        <Button
                          id={`my-application-withdraw-${application.id}`}
                          variant="secondary"
                          size="compact"
                          onClick={() => {
                            setWithdrawError(null);
                            setWithdrawing(application);
                          }}
                        >
                          <FormattedMessage id="share.applications.withdraw" defaultMessage="Zurückziehen" />
                        </Button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        )}
      </div>

      <ConfirmDialog
        id="my-application-withdraw-dialog"
        open={withdrawing !== null}
        title={<FormattedMessage id="share.applications.withdrawTitle" defaultMessage="Bewerbung zurückziehen" />}
        message={
          <>
            <p>
              <FormattedMessage
                id="share.applications.withdrawMessage"
                defaultMessage="Der Rückzug ist endgültig. Eingereichte Dokumente bleiben beim Unternehmen. Solange die Ausschreibung offen ist, kannst du dich neu bewerben."
              />
            </p>
            {withdrawError ? (
              <p role="alert" className="mt-3 text-smycles-meta text-ink-accent">
                {withdrawError}
              </p>
            ) : null}
          </>
        }
        confirmLabel={<FormattedMessage id="share.applications.withdraw" defaultMessage="Zurückziehen" />}
        onConfirm={confirmWithdraw}
        onCancel={() => {
          if (!pending) setWithdrawing(null);
        }}
      />
    </main>
  );
}
